import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Zap, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";

export default function LegalPage({ defaultTab = "terms" }) {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const initialTab = tabFromUrl || defaultTab;
  
  const [terms, setTerms] = useState(null);
  const [privacy, setPrivacy] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [t, p] = await Promise.all([api.get("/legal/terms"), api.get("/legal/privacy")]);
        setTerms(t.data);
        setPrivacy(p.data);
      } catch {
        // ignore
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#003087] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>BandBoost AI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          </Link>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="legal-page">
        <Tabs defaultValue={initialTab}>
          <TabsList className="bg-slate-100 mb-6">
            <TabsTrigger value="terms" data-testid="legal-terms-tab">Terms of Service</TabsTrigger>
            <TabsTrigger value="privacy" data-testid="legal-privacy-tab">Privacy Policy</TabsTrigger>
          </TabsList>
          <TabsContent value="terms">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
              <CardHeader>
                <CardTitle style={{ fontFamily: "Public Sans" }}>{terms?.title || "Terms of Service"}</CardTitle>
                {terms?.last_updated && <p className="text-sm text-slate-500">Last updated: {terms.last_updated}</p>}
              </CardHeader>
              <CardContent>
                {terms?.content ? (
                  <div className="prose max-w-none text-sm text-slate-700">
                    <ReactMarkdown>{terms.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-slate-500">Loading...</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="privacy">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
              <CardHeader>
                <CardTitle style={{ fontFamily: "Public Sans" }}>{privacy?.title || "Privacy Policy"}</CardTitle>
                {privacy?.last_updated && <p className="text-sm text-slate-500">Last updated: {privacy.last_updated}</p>}
              </CardHeader>
              <CardContent>
                {privacy?.content ? (
                  <div className="prose max-w-none text-sm text-slate-700">
                    <ReactMarkdown>{privacy.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-slate-500">Loading...</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
